import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors duration-200 ${
    isActive ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
  }`;

export default function SiteNavbar() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setSearchOpen(false);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2" aria-label="啪啪视频首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-circle-fill text-2xl"></i>
          </span>
          <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">啪啪视频</span>
        </Link>

        {/* Desktop navigation */}
        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          <NavLink to="/" end className={navLinkClass}>
            首页
          </NavLink>
          <div
            className="relative"
            onMouseEnter={() => setCategoryOpen(true)}
            onMouseLeave={() => setCategoryOpen(false)}
          >
            <button
              type="button"
              onClick={() => setCategoryOpen((prev) => !prev)}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap px-3 py-2 text-sm font-medium text-foreground-700 transition-colors duration-200 hover:text-primary-600"
            >
              分类
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${categoryOpen ? "rotate-180" : ""}`}></i>
            </button>
            {categoryOpen ? (
              <div className="absolute left-0 top-full w-64 rounded-lg border border-background-200 bg-background-50 p-2 shadow-sm">
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      onClick={() => setCategoryOpen(false)}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors duration-200 hover:bg-primary-50 hover:text-primary-600"
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <NavLink to="/about" className={navLinkClass}>
            关于我们
          </NavLink>
          <NavLink to="/contact" className={navLinkClass}>
            联系我们
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden items-center lg:flex">
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5">
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索视频"
                className="w-36 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
              <button type="submit" className="cursor-pointer text-sm font-medium text-primary-600">
                搜索
              </button>
            </div>
          </form>

          <button
            type="button"
            onClick={() => setSearchOpen((prev) => !prev)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600 lg:hidden"
            aria-label="打开搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </button>

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors duration-200 hover:border-primary-300 md:hidden"
            aria-label="打开菜单"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {searchOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          <form onSubmit={submitSearch} className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2">
            <i className="ri-search-line text-base text-foreground-400"></i>
            <input
              autoFocus
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="输入关键词搜索视频"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600">
              搜索
            </button>
          </form>
        </div>
      ) : null}

      {mobileOpen ? (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 pb-6 pt-4 md:hidden">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              首页
            </Link>
            <p className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-foreground-400">视频分类</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100"
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
            <Link
              to="/about"
              onClick={() => setMobileOpen(false)}
              className="mt-2 rounded-md px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              关于我们
            </Link>
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              联系我们
            </Link>
          </nav>
        </div>
      ) : null}
    </header>
  );
}